import {useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren} from 'react'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {getTurns, isWorking, needsInput} from '@github-ui/ahp-relay/ahp-session-selectors'
import {DEFAULT_AGENT_MODE, type AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {normalizeReasoningEffort, type ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {
  EstablishedAhpComposerProvider,
  type OnSelectSpace,
} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {ChatState} from '@microsoft/agent-host-protocol'
import type {AhpSpaceSelection} from '@github-ui/ahp-session-client/contracts'

import type {DebugEvent, ProvisioningKind, QuickChatSpaceSelection, RuntimeStatus} from '../types'
import {
  DebugEventsAppendContext,
  DebugEventsDataContext,
  DebugEventsGetterContext,
} from '@github-ui/copilot-shared-ui/hooks/use-debug-events'
import {ModeSelectionContext, ModeUpdateContext} from './ModeContext'
import {
  NativeAhpSessionContext,
  NativeAhpTimelineErrorContext,
  NativeAhpTimelineStateContext,
  ThreadTitleActionsContext,
  ThreadTitleStateContext,
  type ThreadTitleActions,
  type ThreadTitleState,
} from '@github-ui/copilot-shared-ui/contexts/NativeAhpContext'
import {ProvisioningKindContext, RuntimeStatusContext} from './RuntimeContext'
import {
  readStoredMode,
  readStoredReasoningEffort,
  writeStoredMode,
  writeStoredReasoningEffort,
} from '@github-ui/one-copilot-composer/existing'
import {useLatestAgentModel} from '@github-ui/one-copilot-composer/use-latest-agent-model'
import {useAhpDebugLogTool} from './use-ahp-debug-log-tool'

const DEFAULT_DEBUG_EVENTS: DebugEvent[] = []
const MAX_DEBUG_EVENTS = 5000

function createEmptyChatState(): ChatState {
  return {status: 1, turns: []} as unknown as ChatState
}

function getRuntimeStatusFromAhpState(state: ChatState | undefined): RuntimeStatus | undefined {
  if (!state) return undefined
  const latestTurn = getTurns(state).at(-1)
  if (latestTurn && 'state' in latestTurn && latestTurn.state === 'error') return 'error'
  if (needsInput(state)) return 'waiting-for-confirmation'
  if (isWorking(state)) return 'streaming'
  return undefined
}

export interface AhpProviderProps extends PropsWithChildren {
  initialAhpState?: ChatState
  initialSelectedSpace?: AhpSpaceSelection
  initialDebugEvents?: DebugEvent[]
  debugEvents?: readonly DebugEvent[]
  initialRuntimeStatus?: RuntimeStatus
  initialProvisioningKind?: ProvisioningKind
  initialSelectedMode?: AgentMode
  onSessionRenamed?: (taskId: string, title: string) => void
  onUserRename?: () => void
  threadTitleScope?: string
}

/**
 * Provides Chat's render-only state and picker preferences. Active mutations
 * belong to EstablishedAhpSessionProvider and its session handle.
 */
export function AhpProvider({
  children,
  initialAhpState,
  initialSelectedSpace,
  initialDebugEvents = DEFAULT_DEBUG_EVENTS,
  debugEvents,
  initialRuntimeStatus = 'idle',
  initialProvisioningKind,
  initialSelectedMode,
  onSessionRenamed,
  onUserRename,
  threadTitleScope,
}: AhpProviderProps) {
  const [nativeAhpStore] = useState(() => {
    const store = new AhpChatStore()
    store.start()
    store.applySnapshot(initialAhpState ?? createEmptyChatState())
    return store
  })
  const [localDebugEvents, setLocalDebugEvents] = useState<DebugEvent[]>(initialDebugEvents)
  const [threadTitleState, setThreadTitleState] = useState<ThreadTitleState>({userSet: false})
  const [activeThreadTitleScope, setActiveThreadTitleScope] = useState(threadTitleScope)
  if (activeThreadTitleScope !== threadTitleScope) {
    setActiveThreadTitleScope(threadTitleScope)
    setThreadTitleState({userSet: false})
  }
  const [persistedModelId, persistModelId] = useLatestAgentModel()
  const [selectedModelId, setSelectedModelId] = useState(persistedModelId)
  const [previousPersistedModelId, setPreviousPersistedModelId] = useState(persistedModelId)
  // The new-session composer and the established composer share the persisted
  // selection, so an in-place embedded handoff must follow storage changes.
  // Keep local state too: storage can be unavailable or quota-blocked, but a
  // model picked in the established composer must still apply to this session.
  if (persistedModelId !== previousPersistedModelId) {
    setPreviousPersistedModelId(persistedModelId)
    setSelectedModelId(persistedModelId)
  }
  const [selectedSpace, setSelectedSpace] = useState<QuickChatSpaceSelection | undefined>(initialSelectedSpace)
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useState<ReasoningEffort | undefined>(
    // Lazy initializer reads the persisted preference exactly once on mount.
    () => readStoredReasoningEffort(),
  )
  const [selectedMode, setSelectedMode] = useState<AgentMode | undefined>(
    // A visible Interactive selection must also configure the host, otherwise
    // Quick Chat's connect-time allow-all default contradicts the picker.
    // The caller-supplied initial selection (e.g. from the agents composer)
    // wins over the persisted preference so a fresh Sandbox handoff isn't
    // overridden by the last mode the user picked in Quick Chat.
    () => initialSelectedMode ?? readStoredMode() ?? DEFAULT_AGENT_MODE,
  )

  const appendDebugEvent = useCallback((event: DebugEvent) => {
    setLocalDebugEvents(previous => {
      const next = [...previous, event]
      return next.length > MAX_DEBUG_EVENTS ? next.slice(next.length - MAX_DEBUG_EVENTS) : next
    })
  }, [])

  const applyUserThreadTitle = useCallback((title: string) => {
    const trimmedTitle = title.trim()
    setThreadTitleState(trimmedTitle ? {userSet: true, title: trimmedTitle} : {userSet: false})
  }, [])

  const threadTitleActions = useMemo<ThreadTitleActions>(
    () => ({setUserThreadTitle: applyUserThreadTitle, onSessionRenamed, onUserRename}),
    [applyUserThreadTitle, onSessionRenamed, onUserRename],
  )

  const onUpdateModel = useCallback(
    (modelId: string, supportedReasoningEfforts?: readonly string[]) => {
      setSelectedModelId(modelId)
      persistModelId(modelId)
      const normalizedEffort = normalizeReasoningEffort(selectedReasoningEffort, supportedReasoningEfforts)
      if (normalizedEffort !== selectedReasoningEffort) {
        setSelectedReasoningEffort(normalizedEffort)
        writeStoredReasoningEffort(normalizedEffort)
      }
    },
    [persistModelId, selectedReasoningEffort],
  )

  const onUpdateReasoningEffort = useCallback(
    (effort: ReasoningEffort | undefined, supportedReasoningEfforts?: readonly string[]) => {
      const normalizedEffort = normalizeReasoningEffort(effort, supportedReasoningEfforts)
      setSelectedReasoningEffort(normalizedEffort)
      writeStoredReasoningEffort(normalizedEffort)
    },
    [],
  )

  const onSelectSpace = useCallback<OnSelectSpace>(space => {
    setSelectedSpace(space ?? undefined)
  }, [])

  const onUpdateMode = useCallback((mode: AgentMode) => {
    setSelectedMode(mode)
    writeStoredMode(mode)
  }, [])

  const activeDebugEvents = useMemo(() => {
    if (!debugEvents) return localDebugEvents
    if (localDebugEvents.length === 0) return [...debugEvents]
    return [...debugEvents, ...localDebugEvents].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  }, [debugEvents, localDebugEvents])
  useAhpDebugLogTool(activeDebugEvents, undefined)

  const debugEventsRef = useRef(activeDebugEvents)
  useEffect(() => {
    debugEventsRef.current = activeDebugEvents
  }, [activeDebugEvents])
  const getDebugEvents = useCallback(() => debugEventsRef.current, [])

  const nativeAhpState = nativeAhpStore.getSnapshot()
  const runtimeStatus = getRuntimeStatusFromAhpState(initialAhpState) ?? initialRuntimeStatus

  return (
    <DebugEventsDataContext value={activeDebugEvents}>
      <DebugEventsGetterContext value={getDebugEvents}>
        <DebugEventsAppendContext value={appendDebugEvent}>
          <RuntimeStatusContext value={runtimeStatus}>
            <ProvisioningKindContext value={initialProvisioningKind}>
              <ThreadTitleStateContext value={threadTitleState}>
                <ThreadTitleActionsContext value={threadTitleActions}>
                  <EstablishedAhpComposerProvider
                    model={{selectedModelId, onUpdateModel}}
                    reasoningEffort={{selectedReasoningEffort, onUpdateReasoningEffort}}
                    space={{selectedSpace, onSelectSpace}}
                  >
                    <ModeUpdateContext value={onUpdateMode}>
                      <ModeSelectionContext value={selectedMode}>
                        <NativeAhpSessionContext value={undefined}>
                          <NativeAhpTimelineStateContext value={nativeAhpState}>
                            <NativeAhpTimelineErrorContext value={undefined}>{children}</NativeAhpTimelineErrorContext>
                          </NativeAhpTimelineStateContext>
                        </NativeAhpSessionContext>
                      </ModeSelectionContext>
                    </ModeUpdateContext>
                  </EstablishedAhpComposerProvider>
                </ThreadTitleActionsContext>
              </ThreadTitleStateContext>
            </ProvisioningKindContext>
          </RuntimeStatusContext>
        </DebugEventsAppendContext>
      </DebugEventsGetterContext>
    </DebugEventsDataContext>
  )
}
